import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import NextLink from 'next/link';
import { ProductAvailabilityTags, ProductColorSwatches, getProductColorOptions } from '@loomstateorg/shared-frontend';
import type { ProductColorOption } from '@loomstateorg/shared-frontend';
import { IconText } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/greenland-score';
import Prices from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile/prices';
import SustainabilityBadge from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile/sustainability-badge';
import { SustainabilityItem } from 'components/commercetools-ui/types';
import useMediaQuery from '@loomstateorg/shared-frontend/hooks/useMediaQuery';
import useProductTileFeatures from 'helpers/hooks/useProductTileFeatures';
import useVariantWithDiscount from '@loomstateorg/shared-frontend/hooks/useVariantWithDiscount';
import { desktop } from '@loomstateorg/shared-frontend/utils/screensizes';
import { Product, Variant } from 'types/product';
import useImpacts from 'frontastic/hooks/useImpacts';
import Image from 'frontastic/lib/image';
import useTrack from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile/useTrack';

export interface ProductTileProps {
  product: Product;
  onClick?: () => void;
  isSearchResult?: boolean;
  disableVariants?: boolean;
  style?: object;
  sustainability?: {
    certificationsItem?: SustainabilityItem;
    environmentalItem?: SustainabilityItem;
  };
  dataTestId?: string;
  index?: number;
  imageAspectClassName?: string;
  showSecondImageOnHover?: boolean;
  imageApi?: 'fill' | 'layout';
  imageWrapperClassName?: string;
  compactAvailabilityTagClassName?: string;
  compactAvailabilityTagClassNameByKey?: Record<string, string>;
  compactTitleClassName?: string;
  priceRegularFontSize?: 14 | 16;
  priceRegularClassName?: string;
  priceAsHeading?: boolean;
  priceMedium?: boolean;
}

const ProductTileCompact: FC<ProductTileProps> = ({
  product,
  onClick,
  isSearchResult = false,
  disableVariants = false,
  sustainability,
  style,
  dataTestId,
  index = 0,
  imageAspectClassName = 'aspect-[5/7]',
  showSecondImageOnHover = false,
  imageApi = 'fill',
  imageWrapperClassName = '',
  compactAvailabilityTagClassName = 'font-primaryRegular',
  compactAvailabilityTagClassNameByKey = { 'coming-soon': '!text-accent !border-accent font-primaryBold' },
  compactTitleClassName = 'text-md min-w-0 flex-1 overflow-hidden break-words font-primaryBold leading-[1.2] line-clamp-2',
  priceRegularFontSize,
  priceRegularClassName,
  priceAsHeading,
  priceMedium,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);

  const { ref } = useTrack({ product });
  const { getImpactByProduct } = useImpacts();
  const { showColorSwatches } = useProductTileFeatures();
  const [iconsData, setIconData] = useState<IconText[]>([{ icon: <></>, text: '', units: '' }]);

  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;

  const discountedPrice = useMemo(() => variantWithDiscount?.discountedPrice, [variantWithDiscount]);

  const discountPercentage = useMemo(
    () =>
      variantWithDiscount
        ? (((variantWithDiscount.price?.centAmount as number) - (discountedPrice?.centAmount as number)) /
            (variantWithDiscount.price?.centAmount as number)) *
          100
        : 0,
    [discountedPrice, variantWithDiscount],
  );

  const [selectedVariant, setSelectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);

  const colorOptions = useMemo(() => getProductColorOptions(product.variants), [product.variants]);

  const [selectedColorKey, setSelectedColorKey] = useState<string | undefined>(
    () => (selectedVariant?.attributes?.color?.key as string | undefined) ?? colorOptions[0]?.key,
  );

  const [displayedImage, setDisplayedImage] = useState<string | undefined>(() => selectedVariant?.images?.[0]);

  const handleColorSelect = useCallback((color: ProductColorOption) => {
    setSelectedColorKey(color.key);
    // Colors without an image keep the currently displayed one rather than
    // falling back to a placeholder.
    if (color.imageUrl) setDisplayedImage(color.imageUrl);
  }, []);

  const productUrl = useMemo(() => {
    if (!product._url) return '#';

    if (isSearchResult) return `${product._url}?sr=1`;
    return product._url;
  }, [product._url, isSearchResult]);

  const productAvailabilityTags = product?.attributes?.['product-availability-tags'] || [];

  const variantImages = selectedVariant.images ?? [];
  const secondaryImage = variantImages.length > 1 ? variantImages[variantImages.length - 1] : undefined;
  const shouldShowSecondImageOnHover = showSecondImageOnHover && !!secondaryImage;

  useEffect(() => {
    setIconData(getImpactByProduct(product.variants[0], sustainability));
  }, [product.variants, sustainability]);

  const shouldShowSustentability = () => {
    return sustainability?.environmentalItem?.icons?.length && sustainability?.environmentalItem?.shouldShowIcons;
  };

  return (
    <div
      onClick={onClick}
      ref={ref}
      data-testid={dataTestId ?? `product-tile-${index}`}
      className="relative rounded-md flex flex-col h-full"
      style={style}
    >
      <div className="relative">
        <NextLink href={productUrl} data-testid={`product-image-${index}`}>
          <div className={`group relative w-full ${imageAspectClassName} overflow-hidden ${imageWrapperClassName}`}>
            {imageApi === 'layout' ? (
              <Image
                src={displayedImage}
                layout="fill"
                objectFit="cover"
                objectPosition="center"
                priority={true}
                suffix="large"
                alt={product.name || ''}
                className={`rounded-sm ${shouldShowSecondImageOnHover ? 'group-hover:hidden' : ''}`}
              />
            ) : (
              <Image
                src={displayedImage}
                fill
                priority={true}
                suffix="large"
                alt={product.name || ''}
                className={`object-cover object-center rounded-sm ${shouldShowSecondImageOnHover ? 'group-hover:hidden' : ''}`}
              />
            )}
            {shouldShowSecondImageOnHover &&
              (imageApi === 'layout' ? (
                <Image
                  src={secondaryImage}
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center"
                  suffix="large"
                  alt={product.name || ''}
                  className="absolute inset-0 hidden rounded-sm group-hover:block"
                />
              ) : (
                <Image
                  src={secondaryImage}
                  fill
                  suffix="large"
                  alt={product.name || ''}
                  className="absolute inset-0 hidden object-cover object-center rounded-sm group-hover:block"
                />
              ))}
          </div>
        </NextLink>

        {productAvailabilityTags.length > 0 && (
          <div className="absolute right-10 top-10 z-10" data-testid={`availability-tags-${index}`}>
            <ProductAvailabilityTags
              tags={productAvailabilityTags}
              tagClassName={compactAvailabilityTagClassName}
              tagClassNameByKey={compactAvailabilityTagClassNameByKey}
              layout="stacked"
            />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col">
        <NextLink href={productUrl} className="flex flex-col" data-testid={`view-product-${index}`}>
          <div className="flex flex-row items-start gap-8 pt-15">
            <div
              data-testid={dataTestId ? `${dataTestId}-title` : `product-name-${index}`}
              className={compactTitleClassName}
            >
              {product?.name}
            </div>

            <div data-testid={dataTestId ? `${dataTestId}-subtitle` : undefined} className="shrink-0 leading-[1] whitespace-nowrap">
              {selectedVariant?.price?.centAmount && selectedVariant?.price?.centAmount > 0 ? (
                <Prices
                  price={variantWithDiscount?.price ?? selectedVariant?.price}
                  discountedPrice={variantWithDiscount?.discountedPrice}
                  regularFontSize={priceRegularFontSize}
                  regularClassName={priceRegularClassName}
                  asHeading={priceAsHeading}
                  medium={priceMedium}
                  index={index}
                />
              ) : (
                <div className="h-21"></div>
              )}
            </div>
          </div>
        </NextLink>

        {showColorSwatches && !disableVariants && (
          <ProductColorSwatches
            colors={colorOptions}
            selectedColorKey={selectedColorKey}
            onSelect={handleColorSelect}
            moreHref={productUrl}
            linkComponent={NextLink}
            className="pt-12"
          />
        )}

        {shouldShowSustentability() && (
          <SustainabilityBadge
            iconsWithText={iconsData}
            environmentalTextColor={sustainability?.environmentalItem?.textColor}
            className="mt-auto pt-12"
            index={index}
          />
        )}
      </div>
    </div>
  );
};

export default ProductTileCompact;
